import React from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getDriverColor } from '../utils/routeUtils';
import { getIconColor, getIconLabel, getIconLabelColor, getIconBorderColor } from './map/MapIcons';

function DraggableRouteItem({ point }) {
    let color = getIconColor(point);
    let label = getIconLabel(point);  
    let labelColor = getIconLabelColor(point);
    let borderColor = getIconBorderColor(point);
  return (
    <div className="flex items-center">
      {/* Circle with number */}
      <div className="flex-shrink-0 mr-2">
        <span className="flex items-center justify-center h-8 w-8 rounded-full text-white text-sm"
              style={{ backgroundColor: color, color: labelColor, border: `4px solid ${borderColor}`, fontWeight: 'bold'}}>
          {point.isHome ? 'H' : point.displayOrder}
        </span>
      </div>
      {/* Point details */}
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-gray-900 truncate">
          {point.isHome ? 'HOME' : (
            point['Delivery Name'] === 'DEPO'
              ? 'DEPO'
              : (point.isGroup && point.shipmentCount > 1
                ? `${point.shipmentCount} shipments`
                : point['Delivery Name'])
          )}
        </p>
        <p className="text-xs text-gray-500 truncate">
          {point['Formatted Address']}
        </p>
      </div>
    </div>
  );
}

function SortableRouteItem({ id, point, index, disabled, activeId }) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id, disabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    boxShadow: isDragging ? '0 2px 8px rgba(0,0,0,0.15)' : undefined,
    cursor: 'grab',
    opacity: isDragging ? 0.7 : 1,
  };

  return (
    <li
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`px-4 py-3 bg-white hover:bg-gray-50 ${isDragging ? 'ring-2 ring-indigo-500' : ''}`}
    >
      <DraggableRouteItem point={point} />
    </li>
  );
}

function DriverRouteDndList({ points, onReorder, disabledIds = [] }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } })
  );

  // dnd-kit expects unique string ids
  const items = points.map((p, i) => ({ ...p, dndId: `${p.id}-${p.sequence}` }));

  const [activeId, setActiveId] = React.useState(null);
  const [currentOrder, setCurrentOrder] = React.useState(items.map(i => i.dndId));

  React.useEffect(() => {
    setCurrentOrder(items.map(i => i.dndId));
  }, [points]);

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event) {
    const { active, over } = event;
    setActiveId(null);
    if (over && active.id !== over.id) {
      const oldIndex = currentOrder.indexOf(active.id);
      const newIndex = currentOrder.indexOf(over.id);
      const newOrder = arrayMove(currentOrder, oldIndex, newIndex);
      console.log('newOrder', newOrder);
      setCurrentOrder(newOrder);
      // Map new order to points
      const reordered = newOrder.map(dndId => items.find(i => i.dndId === dndId));
      onReorder(reordered);
    }
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCenter}
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
    >
      <SortableContext items={currentOrder} strategy={verticalListSortingStrategy}>
        <ul className="divide-y divide-gray-200">
          {currentOrder.map((dndId, index) => {
            // Find the corresponding point
            const point = items.find(i => i.dndId === dndId);
            // If no matching point, skip
            if (!point) return null;
            return (
              <SortableRouteItem
                key={dndId}
                id={dndId}
                point={point}
                index={index}
                disabled={point.isHome || disabledIds.includes(point.id)}
                activeId={activeId}
              />
            );
          })}
        </ul>
      </SortableContext>
    </DndContext>
  );
}

export default DriverRouteDndList; 